import { CSSProperties, FC, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';

import useTheme from 'api/useTheme';
import { PREVIEW_CONTAINER_HEIGHT } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import {
  BASE_CONTAINER_STYLE,
  CONTAINER_STYLE_MAP,
  PREVIEW_ELEMENT_ID,
  RENDER_MAP
} from './OfferDesignPreview.consts';
import { applyCssVariables, clearCssVariables } from './OfferDesignPreview.utils';

interface OfferDesignPreviewProps {
  offerType: OfferDesignTypes;
  values: Record<string, any>;
}

export const OfferDesignPreview: FC<OfferDesignPreviewProps> = ({
  offerType,
  values
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { publisherId } = useParams();
  const { getGeneralTheme: generalTheme, getStoreScreen: storeScreen } = useTheme(publisherId);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !storeScreen.data) return;

    applyCssVariables(el, storeScreen.data, generalTheme.data?.font || '');
    return () => clearCssVariables(el);
  }, [storeScreen.data, generalTheme.data?.font]);

  const baseContainerStyle = CONTAINER_STYLE_MAP[offerType] ?? BASE_CONTAINER_STYLE;
  const isPopup = offerType === OfferDesignTypes.PopUp;

  // TODO: Temporary CSS override solution for Popup preview (aligned with Rony). To be replaced with a proper shared-ui fix.
  const containerStyle = useMemo<CSSProperties>(() => ({
    ...baseContainerStyle,
    ...(isPopup && {
      '--popup-bg-color': values.backgroundColor?.colorOne || ''
    })
  } as CSSProperties), [baseContainerStyle, isPopup, values.backgroundColor?.colorOne]);

  const popupOverrideClasses = isPopup
    ? '[&_.MuiBackdrop-root]:!bg-transparent [&_.daily-bonus-border]:!filter-none [&_.daily-bonus-border]:!shadow-none [&_.daily-bonus-banner]:![background-color:var(--popup-bg-color)]'
    : '';

  if (generalTheme.isLoading || storeScreen.isLoading || !generalTheme.data || !storeScreen.data) return null;

  const renderCard = RENDER_MAP[offerType];
  if (!renderCard) return null;

  return (
    <div className="w-1/2 h-full bg-zinc-100 px-16 pt-9 pb-8 overflow-y-auto">
      <div className="flex justify-center items-center" style={{ minHeight: PREVIEW_CONTAINER_HEIGHT }}>
        <div id={PREVIEW_ELEMENT_ID} ref={containerRef} style={containerStyle} className={popupOverrideClasses}>
          {renderCard(values, generalTheme.data, storeScreen.data)}
        </div>
      </div>
    </div>
  );
};
